// src/components/auth/login/Login.js

import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { auth } from "../../../firebase.js";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Building,
} from "lucide-react";
import { getEmailVerificationActionCodeSettings } from "../../../auth/authState.js";
import {
  ensureOwnerProfileForAuthUser,
  fetchAndCacheUserProfile,
} from "../../../auth/ownerProfile.js";

const normalizeUserStatus = (value) => (value || "").toString().trim().toLowerCase();

const isDeactivatedUserProfile = (profile) => {
  if (!profile) return false;
  if (profile.disabled === true) return true;
  return ["inactive", "disabled", "deactivated"].includes(
    normalizeUserStatus(profile.status)
  );
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);

  const [verificationSent, setVerificationSent] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  const [infoMessage, setInfoMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const pitchUrl = `${process.env.PUBLIC_URL}/landing/thepitch.png`;

  const setupUserProfile = async (user) => {
    return ensureOwnerProfileForAuthUser(user);
  };

  const fetchUserProfile = async (uid) => {
    return fetchAndCacheUserProfile(uid);
  };

  const handleReturnToLogin = async () => {
    try {
      if (auth.currentUser) {
        await signOut(auth);
      }
    } catch (e) {
      // ignore
    }

    setPassword("");
    setError("");
    setShowResend(false);
    setVerificationSent(false);
    setSendingVerification(false);
    setInfoMessage("");

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams(location.search || "");
      const verified = params.get("verified");
      const unverified = params.get("unverified");

      setError("");
      setShowResend(false);
      setVerificationSent(false);
      setSendingVerification(false);

      if (verified === "1") {
        setInfoMessage("✅ Email verified! Please log in to continue.");

        try {
          if (auth.currentUser) {
            await signOut(auth);
          }
        } catch (e) {
          // ignore
        }

        return;
      }

      if (unverified === "1") {
        setInfoMessage("Please verify your email before you can access the app.");
      } else {
        setInfoMessage("");
      }

      const current = auth.currentUser;
      if (!current) return;

      try {
        await current.reload();
      } catch (e) {
        // ignore
      }
      if (cancelled) return;

      if (!current.emailVerified) {
        setEmail(current.email || "");
        setError(
          "Your email is not verified yet. Please open the verification email and click the link. " +
            "If you can't find it, resend it below."
        );
        setShowResend(true);
      } else {
        setError("");
        setShowResend(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setVerificationSent(false);
    setSendingVerification(false);
    setInfoMessage("");

    try {
      const trimmedEmail = (email || "").trim().toLowerCase();

      const userCredential = await signInWithEmailAndPassword(
        auth,
        trimmedEmail,
        password
      );

      const user = userCredential.user;

      await user.reload();

      if (!user.emailVerified) {
        setError(
          "Your email is not verified yet. Please open the verification email we sent when you signed up. " +
            "If you can't find it, click 'Resend Verification Email' below."
        );
        setShowResend(true);
        return;
      }

      await setupUserProfile(user);
      const profile = await fetchUserProfile(user.uid);

      if (isDeactivatedUserProfile(profile)) {
        await signOut(auth);
        setError("Your account has been deactivated. Please contact your company owner.");
        return;
      }

      navigate("/");
    } catch (err) {
      console.error("Login error:", err);

      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/user-not-found"
      ) {
        setError(
          "Incorrect email or password. " +
            "If you just created this account, make sure you typed your password exactly as you set it."
        );
      } else if (err.code === "auth/too-many-requests") {
        setError(
          "Too many login or email attempts. Firebase has temporarily blocked this account. " +
            "Please wait a few minutes and try again."
        );
      } else if (err.code === "auth/user-disabled") {
        setError("Your account has been deactivated. Please contact your company owner.");
      } else {
        setError("Login failed: " + (err.message || "Unknown error"));
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      if (sendingVerification) return;

      const currentUser = auth.currentUser;

      if (!currentUser) {
        alert(
          "We couldn't find a signed-in user.\n\n" +
            "Please log in with your email and password. " +
            "If the login succeeds and your email is still unverified, " +
            "you'll see the option to resend the verification email."
        );
        return;
      }

      setSendingVerification(true);

      console.log("🔎 Attempting to send verification email...");
      console.log("📧 User email:", currentUser.email);

      await sendEmailVerification(currentUser, getEmailVerificationActionCodeSettings());

      console.log("✅ Verification email SENT");

      setVerificationSent(true);
      setShowResend(false);
      setError("");
    } catch (err) {
      console.error("❌ Failed to resend verification email:", err);
      if (err.code === "auth/too-many-requests") {
        setError(
          "You've requested verification too many times in a short period. " +
            "Firebase has temporarily blocked sending more emails. Please wait a while and try again."
        );
      } else {
        setError("Error: " + (err.message || "Unknown error"));
      }
    } finally {
      setSendingVerification(false);
    }
  };

  // ============================================================================
  // ✅ FIXED: Social login now works for BOTH signup and login
  // ============================================================================
  const handleSocialLogin = async (providerType) => {
    let provider;

    if (providerType === "google") {
      provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
    } else if (providerType === "microsoft") {
      provider = new OAuthProvider("microsoft.com");
    } else {
      setError("Unknown provider.");
      return;
    }

    setError("");
    setInfoMessage("");

    try {
      console.log(`[Login] Starting ${providerType} OAuth`);

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log(`[Login] ${providerType} OAuth success:`, user.email);

      // ✅ setupUserProfile handles BOTH new signups and existing logins
      await setupUserProfile(user);
      await fetchUserProfile(user.uid);

      console.log(`[Login] ✅ ${providerType} login complete - navigating to app`);
      navigate("/");
    } catch (error) {
      console.error(`${providerType} login error:`, error);

      // Handle common OAuth errors
      if (error.code === "auth/popup-closed-by-user") {
        setError("Sign-in cancelled. Please try again.");
      } else if (error.code === "auth/account-exists-with-different-credential") {
        setError(
          "An account already exists with this email using a different sign-in method. " +
            "Please try logging in with your original method."
        );
      } else if (error.code === "auth/popup-blocked") {
        setError("Pop-up was blocked by your browser. Please allow pop-ups for this site and try again.");
      } else if (error.code === "auth/invalid-credential") {
        setError(
          `${providerType.charAt(0).toUpperCase() + providerType.slice(1)} authentication failed. ` +
            "This may be due to incorrect OAuth configuration. Please contact support or try a different sign-in method."
        );
      } else {
        setError(
          `${providerType.charAt(0).toUpperCase() + providerType.slice(1)} login failed: ${
            error.message || "Unknown error"
          }`
        );
      }
    }
  };

  return (
    <div className="min-h-screen w-full font-sans bg-slate-950">
      <style>{`
        .sh-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-repeat: no-repeat;
          background-position: 50% 50%;
          transform: scale(1.08);
          animation: sh-pan 16s ease-in-out infinite;
          will-change: background-position, transform;
        }

        @keyframes sh-pan {
          0%   { background-position: 48% 52%; transform: scale(1.08); }
          50%  { background-position: 56% 48%; transform: scale(1.11); }
          100% { background-position: 48% 52%; transform: scale(1.08); }
        }

        @media (prefers-reduced-motion: reduce) {
          .sh-bg { animation: none; }
        }
      `}</style>

      <div className="flex flex-col md:flex-row min-h-screen">
        {/* Left Panel - Pitch Image */}
        <div className="relative md:w-1/2 hidden md:flex overflow-hidden">
          <div
            className="sh-bg"
            style={{ backgroundImage: `url(${pitchUrl})` }}
            aria-hidden="true"
          />

          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 via-slate-950/55 to-slate-950/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-transparent" />

          <div className="relative z-10 p-10 flex flex-col justify-between w-full">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
                Sampling • CRM • Delivery Ops
              </div>

              <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-white">
                Bring easy sampling to your customers — and tools to your entire team.
              </h2>

              <p className="mt-3 text-base leading-relaxed text-white/75 max-w-xl">
                Metrics, analytics, and loss tracking built for real workflows. A clean experience for
                customers, reps, managers, and admins — with a free mobile app for everyone.
              </p>

              <div className="mt-6 grid gap-3 max-w-xl">
                <div className="flex gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
                  <div className="mt-0.5 h-5 w-5 rounded-md border border-emerald-400/40 bg-emerald-400/15 flex items-center justify-center text-emerald-200 text-xs font-bold">
                    ✓
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Free mobile app for staff + customers</div>
                    <div className="text-sm text-white/70">Everyone stays aligned from pitch to feedback.</div>
                  </div>
                </div>

                <div className="flex gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
                  <div className="mt-0.5 h-5 w-5 rounded-md border border-emerald-400/40 bg-emerald-400/15 flex items-center justify-center text-emerald-200 text-xs font-bold">
                    ✓
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Analytics + loss tracking</div>
                    <div className="text-sm text-white/70">Spot pitfalls early and improve outcomes with real data.</div>
                  </div>
                </div>

                <div className="flex gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
                  <div className="mt-0.5 h-5 w-5 rounded-md border border-emerald-400/40 bg-emerald-400/15 flex items-center justify-center text-emerald-200 text-xs font-bold">
                    ✓
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Affordable CRM Lite</div>
                    <div className="text-sm text-white/70">We specialize in your business.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-white/60 text-sm">
              © {new Date().getFullYear()} The Supply Hawk
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="md:w-1/2 flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <div className="md:hidden mb-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
                The Supply Hawk
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="mx-auto bg-gradient-to-r from-blue-600 to-indigo-600 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <Building className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
                  <p className="text-gray-600 mt-2">
                    Log in to manage sampling, accounts, and operations.
                  </p>
                </div>

                {infoMessage && (
                  <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-sm">
                    {infoMessage}
                  </div>
                )}

                {verificationSent && (
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center justify-center gap-2 text-emerald-700 text-sm font-medium">
                      <CheckCircle2 className="w-5 h-5" />
                      Verification email sent — check your inbox.
                    </div>

                    <button
                      type="button"
                      onClick={handleReturnToLogin}
                      className="w-full border border-gray-200 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                    >
                      Return to Login
                    </button>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 outline-none placeholder-gray-400"
                      placeholder="you@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 outline-none placeholder-gray-400"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      Login
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>

                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <p className="text-red-800 text-sm">{error}</p>
                    </div>
                  )}

                  {showResend && !verificationSent && (
                    <div className="text-center space-y-3">
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleResendVerification}
                        disabled={sendingVerification}
                      >
                        <Mail className="w-4 h-4" />
                        {sendingVerification
                          ? "Sending verification email..."
                          : "Resend Verification Email"}
                      </button>

                      <button
                        type="button"
                        onClick={handleReturnToLogin}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                      >
                        Return to Login
                      </button>
                    </div>
                  )}
                </form>

                <div className="my-6 flex items-center">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="mx-4 text-gray-500 text-sm">OR</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={() => handleSocialLogin("google")}
                    className="w-full flex items-center justify-center py-3 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      className="w-6 h-6 mr-3"
                    />
                    <span className="font-medium text-gray-700">
                      Continue with Google
                    </span>
                  </button>

                  <button
                    onClick={() => handleSocialLogin("microsoft")}
                    className="w-full flex items-center justify-center py-3 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                      alt="Microsoft"
                      className="w-6 h-6 mr-3"
                    />
                    <span className="font-medium text-gray-700">
                      Continue with Microsoft
                    </span>
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 border-t border-gray-100 p-4 text-center">
                <p className="text-gray-600 text-sm">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    Sign up here
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-4 text-center text-xs text-white/50">
              By logging in, you agree to your organization's access policies.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
